import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import supabase from '../lib/supabase';
import usePageTitle from '../hooks/usePageTitle';
import { fetchAppSettings } from '../hooks/useAppSettings';
import '../styles/Auth.css';

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
      <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"/>
      <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"/>
      <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"/>
      <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"/>
    </g>
  </svg>
);

const Auth = ({ initialTab = 'login' }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const redirectTo = params.get('redirect') || '/';

  const [mode, setMode] = useState(initialTab === 'signup' ? 'signup' : 'login');
  usePageTitle(mode === 'signup' ? 'Create Account' : 'Sign In');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [signupComplete, setSignupComplete] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [requireEmailConfirmation, setRequireEmailConfirmation] = useState(true);

  const { signIn, signUp } = useAuth();
  // eslint-disable-next-line no-unused-vars -- 3.4.2026
  const [forgotMode, setForgotMode] = useState(false);
  // eslint-disable-next-line no-unused-vars -- 3.4.2026
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    setMessage({ text: '', type: '' });
    const googleAuthError = localStorage.getItem('googleAuthError');
    if (googleAuthError) {
      try {
        const errorData = JSON.parse(googleAuthError);
        setMessage({ text: `Google sign-in failed: ${errorData.message}`, type: 'error' });
      } catch (e) {}
      localStorage.removeItem('googleAuthError');
    }
    fetchAppSettings().then(s => {
      setRequireEmailConfirmation(s.require_email_confirmation !== false);
    });
  }, []);

  const switchMode = (newMode) => {
    setMode(newMode);
    setForgotMode(false);
    setResetSent(false);
    setMessage({ text: '', type: '' });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setMessage({ text: 'Enter your email address above first.', type: 'error' });
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/login',
    });
    setLoading(false);
    if (error) {
      setMessage({ text: error.message, type: 'error' });
    } else {
      setResetSent(true);
      setForgotMode(false);
      setMessage({ text: 'Password reset email sent! Check your inbox.', type: 'success' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      if (mode === 'login') {
        const { error } = await signIn(email, password);
        if (error) {
          // Generic error message for security - don't reveal if email exists or not
          setMessage({
            text: 'The email and password you entered don\'t match our records. Please verify and try again.',
            type: 'error',
            showSignupLink: true,
          });
          return;
        }
        navigate(redirectTo);
      } else {
        // Validate full name minimum 3 characters
        if (fullName.trim().length < 3) {
          setMessage({ text: 'Full name must be at least 3 characters long.', type: 'error' });
          setLoading(false);
          return;
        }
        
        const { data, error } = await signUp(email, password, fullName);
        if (error) throw error;
        setSignupEmail(email);
        if (data?.session === null) {
          setSignupComplete(true);
        } else {
          navigate(redirectTo);
        }
      }
    } catch (err) {
      setMessage({ text: err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const redirectBaseUrl = window.location.origin;
      localStorage.setItem('authRedirectUrl', redirectBaseUrl + redirectTo);
      localStorage.setItem('googleAuthInProgress', 'true');
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectBaseUrl,
          scopes: 'email profile',
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) throw error;
    } catch (err) {
      setMessage({ text: `Google sign-in failed: ${err.message}`, type: 'error' });
      setLoading(false);
    }
  };

  if (signupComplete) {
    return (
      <div className="auth-page">
        <div className="auth-card auth-card-confirm">
          {requireEmailConfirmation ? (
            <>
              <div className="auth-confirm-icon"><Mail size={48} aria-hidden="true" /></div>
              <h2>Check Your Email</h2>
              <p className="auth-confirm-lead">We sent a confirmation link to:</p>
              <p className="auth-confirm-email">{signupEmail}</p>
              <p className="auth-confirm-body">
                Click the link in that email to activate your account. It comes from
                <br /><strong>The Icing Calculator</strong>
                <br /><span className="auth-confirm-sender">admin@mail.theicingcalculator.com</span>
              </p>
              <p className="auth-confirm-hint">Don't see it? Be sure to check your <strong>spam or junk folder</strong>.</p>
              <a href="/" className="auth-btn auth-btn-primary auth-confirm-btn">Back to Home</a>
            </>
          ) : (
            <>
              <div className="auth-confirm-icon"><CheckCircle2 size={48} aria-hidden="true" /></div>
              <h2>You're all set!</h2>
              <p className="auth-confirm-lead">Welcome to The Icing Calculator.</p>
              <p className="auth-confirm-body">
                A welcome email is on its way to <strong>{signupEmail}</strong>.
                If you don't see it, check your <strong>spam or junk folder</strong>.
              </p>
              <p className="auth-confirm-hint">You can start using the calculator right now.</p>
              <a href={redirectTo} className="auth-btn auth-btn-primary auth-confirm-btn">Start Calculating →</a>
            </>
          )}
        </div>
      </div>
    );
  }

  const contextMsg =
    redirectTo === '/pro' ? 'Sign in to save your plan, export, or use the AI tools. The calculator itself stays free.' : null;

  return (
    <div className="auth-page">
      <div className="auth-card">

        {/* Header */}
        <div className="auth-header">
          <a href="/" className="auth-back-link">← Back to Home</a>
          <img
            src={`${process.env.PUBLIC_URL}/images/Icing_Calc_logo_May2026.png`}
            alt="The Icing Calculator"
            className="auth-logo"
          />
          <h1 className="auth-title">The Icing Calculator</h1>
          <p className="auth-free-note">Pro Calculator features are free right now. No card required.</p>
          {contextMsg && <p className="auth-context-msg">{contextMsg}</p>}
        </div>

        {/* Google — works for everyone, new or returning */}
        <button
          className="auth-btn auth-btn-google"
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          <GoogleIcon />
          Continue with Google
        </button>
        <p className="auth-google-note">Works whether you're new or returning.</p>

        {/* Divider */}
        <div className="auth-divider"><span>or sign in with email</span></div>

        {/* Mode toggle */}
        <div className="auth-mode-toggle">
          <button
            className={`auth-mode-btn${mode === 'login' ? ' active' : ''}`}
            onClick={() => switchMode('login')}
            type="button"
          >
            Sign In
          </button>
          <button
            className={`auth-mode-btn${mode === 'signup' ? ' active' : ''}`}
            onClick={() => switchMode('signup')}
            type="button"
          >
            Create Account
          </button>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`auth-message auth-message-${message.type}`}>
            {message.text}
            {message.showSignupLink && (
              <button
                type="button"
                className="auth-inline-link"
                onClick={() => switchMode('signup')}
              >
                Create a free account →
              </button>
            )}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          {mode === 'signup' && (
            <div className="auth-field">
              <label htmlFor="signupFullName">Full Name</label>
              <input
                id="signupFullName"
                type="text"
                placeholder="Jane Smith"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                disabled={loading}
                autoComplete="name"
                minLength={3}
              />
            </div>
          )}

          <div className="auth-field">
            <label htmlFor={mode === 'signup' ? 'signupEmail' : 'loginEmail'}>Email</label>
            <input
              id={mode === 'signup' ? 'signupEmail' : 'loginEmail'}
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              autoComplete={mode === 'signup' ? 'email' : 'email'}
            />
          </div>

          <div className="auth-field">
            <label htmlFor={mode === 'signup' ? 'signupPassword' : 'loginPassword'}>
              Password
              {mode === 'signup' && <span className="auth-field-hint"> (min 6 characters)</span>}
            </label>
            <input
              id={mode === 'signup' ? 'signupPassword' : 'loginPassword'}
              type="password"
              placeholder={mode === 'signup' ? 'Create a password' : 'Your password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              minLength={6}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            />
          </div>

          {mode === 'login' && (
            <p className="auth-forgot-link">
              <button
                type="button"
                className="auth-inline-link"
                onClick={handleForgotPassword}
                disabled={loading}
              >
                Forgot your password?
              </button>
            </p>
          )}

          <button
            type="submit"
            className="auth-btn auth-btn-primary"
            disabled={loading || (mode === 'signup' && fullName.trim().length < 3)}
          >
            {loading ? 'Please wait…' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

      </div>
    </div>
  );
};

export default Auth;
